import "./ViewProductOrder.scss";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { OrderColumns } from "../../../SourceData";
import { toast } from "react-toastify";
import ColorButton from "../../../components/ColorButton/ColorButton";
import Avatr from "./../../../assets/avatar.svg";
import Location from "./../../../assets/location-icon.png";
import PrintIcon from "@mui/icons-material/Print";
import NotificationsIcon from "@mui/icons-material/NotificationsActive";
import CancelIcon from "@mui/icons-material/Cancel";
import Stepper from "../../../components/Stepper/Stepper";
import api from "../../../utils/api";
import Alert from "@mui/material/Alert";
import Loader from "../../../components/Loader";
import LocationMapDialog from "../../../components/LocationMapDialog/LocationMapDialog";

const ViewProductOrder = () => {
  const { id } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState({});
  const [error, setError] = useState(null);
  const [refetch, setRefetch] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // coupon helpers
  const couponCode = orderData?.coupon?.code ?? null;
  const couponDiscountAmount = orderData?.coupon?.discount_amount ?? null;

  const STATUS_IDS = {
    WAITING_APPROVAL: 1,
    PROCESSING: 2,
    DELIVERING: 3,
    DELIVERED: 4,
  };
  const isWholeOrderCanceled = () => orderData?.status === "canceled";

  const getCurrentStatusId = (arr) => {
    if (!Array.isArray(arr)) return null;
    const selected = arr.filter((s) => s.is_select === true);
    if (!selected.length) return null;
    return Math.max(...selected.map((s) => s.id));
  };

  const canCancelOrder = (vp) => {
    if (isWholeOrderCanceled()) return false;
    const id = getCurrentStatusId(vp?.order_status);
    return !vp?.cancel_order && id !== STATUS_IDS.DELIVERED;
  };

  const isOrderDelivered = (vp) =>
    getCurrentStatusId(vp?.order_status) === STATUS_IDS.DELIVERED;
  const canSendNotification = (vp) =>
    !isWholeOrderCanceled() && !vp?.cancel_order && !isOrderDelivered(vp);
  const canCancelWholeOrder = () =>
    !isWholeOrderCanceled() && orderData?.status !== "Delivered";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} - ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const handleLocationClick = () => setLocationDialogOpen(true);
  const handleCloseLocationDialog = () => setLocationDialogOpen(false);

  // PRINT (removed per-item discount column; added coupon rows under products value)
  const generatePrintContent = (vendorProduct = null) => {
    const isVendorSpecific = vendorProduct !== null;
    const products = isVendorSpecific
      ? vendorProduct.products
      : orderData?.vendor_products?.flatMap((vp) => vp.products) || [];

    const vendorName = isVendorSpecific
      ? vendorProduct?.vendor?.name || "غير محدد"
      : "جميع التجار";
    const orderStatusText = isWholeOrderCanceled() ? " (ملغي)" : "";

    const subtotalPrint = isVendorSpecific
      ? products.reduce((sum, p) => sum + (parseFloat(p?.price) || 0), 0)
      : orderData?.subtotal || 0;

    const taxPrint = isVendorSpecific ? 0 : orderData?.tax || 0;
    const deliveryPrint = isVendorSpecific ? 0 : orderData?.delivery_cost || 0;
    const totalPrint = isVendorSpecific ? subtotalPrint : orderData?.total || 0;

    const couponHTML =
      couponCode || couponDiscountAmount
        ? `
        <div class="summary-row">
          <span class="summary-label">كود القسيمة</span>
          <span>${couponCode ?? "-"}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">الخصم على الفاتورة</span>
          <span>${couponDiscountAmount ?? 0} جنية</span>
        </div>`
        : "";

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>فاتورة رقم ${
          orderData?.order_number || ""
        }${orderStatusText}</title>
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:Arial,sans-serif;direction:rtl;padding:20px;background:#fff;color:#333}
          .invoice-container{max-width:800px;margin:0 auto;background:#fff;border:1px solid #ddd;border-radius:8px;overflow:hidden}
          .invoice-header{background:${
            isWholeOrderCanceled() ? "#ffebee" : "#f8f9fa"
          };padding:20px;border-bottom:2px solid #e9ecef;text-align:center}
          .invoice-title{font-size:28px;font-weight:bold;color:${
            isWholeOrderCanceled() ? "#d32f2f" : "#2c3e50"
          };margin-bottom:10px}
          .canceled-badge{background:#f44336;color:#fff;padding:8px 16px;border-radius:20px;font-size:14px;font-weight:bold;margin-bottom:10px;display:inline-block}
          .invoice-details{display:flex;justify-content:space-between;padding:20px;border-bottom:1px solid #eee}
          .invoice-info{flex:1}
          .store-info{text-align:left;flex:1}
          .products-table{width:100%;border-collapse:collapse;margin:20px 0}
          .products-table th,.products-table td{border:1px solid #ddd;padding:12px;text-align:center}
          .products-table th{background:#f8f9fa;font-weight:bold;color:#2c3e50}
          .products-table tbody tr:nth-child(even){background:#f9f9f9}
          .summary-section{padding:20px;border-top:2px solid #e9ecef;background:#f8f9fa}
          .summary-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #dee2e6}
          .summary-row:last-child{border-bottom:none;font-weight:bold;font-size:18px;color:#2c3e50}
          .summary-label{font-weight:500}
          .footer{text-align:center;padding:20px;color:#6c757d;font-size:14px}
          @media print{body{padding:0}.invoice-container{border:none;box-shadow:none}}
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <h1 class="invoice-title">الفاتورة${orderStatusText}</h1>
            ${
              isWholeOrderCanceled()
                ? '<div class="canceled-badge">طلب ملغي</div>'
                : ""
            }
          </div>

          <div class="invoice-details">
            <div class="invoice-info">
              <h3>رقم الطلب: ${orderData?.order_number || "غير محدد"}</h3>
              <p>${formatDate(orderData?.created_at)}</p>
              <p>طريقة الدفع: ${
                orderData?.payment_type === "visa"
                  ? "فيزا"
                  : orderData?.payment_type || "غير محدد"
              }</p>
              ${
                isWholeOrderCanceled()
                  ? '<p style="color:#d32f2f;font-weight:bold">حالة الطلب: ملغي</p>'
                  : ""
              }
            </div>
            <div class="store-info">
              <h3>${vendorName}</h3>
              <p>عدد المنتجات: ${products.length}</p>
            </div>
          </div>

          <div style="padding:0 20px">
            <table class="products-table">
              <thead>
                <tr>
                  <th>اسم المنتج</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                </tr>
              </thead>
              <tbody>
                ${products
                  .map(
                    (p) => `
                  <tr>
                    <td>${p?.name || "غير محدد"}</td>
                    <td>${p?.qty || 0}</td>
                    <td>${p?.price || 0} جنية</td>
                  </tr>`
                  )
                  .join("")}
              </tbody>
            </table>
          </div>

          <div class="summary-section">
            <div class="summary-row">
              <span class="summary-label">قيمة المنتجات</span>
              <span>${subtotalPrint} جنية</span>
            </div>
            ${couponHTML}
            <div class="summary-row">
              <span class="summary-label">الضريبة</span>
              <span>${taxPrint} جنية</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">تكلفة التوصيل</span>
              <span>${deliveryPrint} جنية</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">الإجمالي</span>
              <span>${totalPrint} جنية</span>
            </div>
          </div>

          <div class="footer">
            <p>شكراً لتعاملكم معنا</p>
            <p>تاريخ الطباعة: ${formatDate(new Date().toISOString())}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handlePrintInvoice = (vendorProduct = null) => {
    const html = generatePrintContent(vendorProduct);
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.onload = () => {
      w.print();
      w.close();
    };
  };

  const handleSendNotification = async (vendorId) => {
    if (!vendorId || !id) return toast.error("معرف الطلب أو التاجر غير متوفر");
    if (isWholeOrderCanceled())
      return toast.error("لا يمكن إرسال تنبيه لطلب ملغي");

    setNotificationLoading((prev) => ({ ...prev, [vendorId]: true }));
    try {
      const res = await api.post("/changeStatusOrder", {
        order_id: id,
        vendor_id: vendorId,
      });
      if (res?.data?.status === 200 || res?.status === 200)
        toast.success("تم ارسال التنبيه الي البائع");
      else throw new Error("فشل في إرسال التنبيه");
    } catch (err) {
      toast.error("حدث خطأ أثناء إرسال التنبيه، برجاء المحاولة مرة أخرى");
    } finally {
      setNotificationLoading((prev) => ({ ...prev, [vendorId]: false }));
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/orders/${id}`);
        if (res?.data?.status === 200) setOrderData(res.data.data);
        else setError("No order data found");
      } catch (err) {
        setError("Failed to fetch order data");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchOrder();
    else {
      setError("Order ID is missing");
      setLoading(false);
    }
  }, [id, refetch]);

  const handleCancelWholeOrder = async () => {
    if (isWholeOrderCanceled()) return toast.error("الطلب ملغي بالفعل");
    setActionLoading(true);
    try {
      await api.post("/cancelOrder", { id });
      toast.success("تم الغاء الطلب بنجاح");
    } catch (err) {
      toast.error("حدث خطاء ماء برجاء المحاولة مرة اخري");
    } finally {
      setActionLoading(false);
      setRefetch((v) => !v);
    }
  };

  const handleCancelVendorOrder = async (vendorId) => {
    if (isWholeOrderCanceled())
      return toast.error("لا يمكن إلغاء طلب فردي لطلب ملغي بالكامل");
    setActionLoading(true);
    try {
      await api.post("/cancelOrder", { id, vendor_id: vendorId });
      toast.success("تم الغاء طلب هذا التاجر بنجاح");
    } catch (err) {
      toast.error("حدث خطاء ماء برجاء المحاولة مرة اخري");
    } finally {
      setRefetch((v) => !v);
      setActionLoading(false);
    }
  };

  // Remove any discount column from the visible table
  const filteredOrderColumns = useMemo(() => {
    const cols = Array.isArray(OrderColumns) ? OrderColumns : [];
    return cols.filter((col) => {
      const header = (col?.headerName ?? "").toString();
      const field = (col?.field ?? "").toString();
      const isDiscountHeader =
        header.includes("خصم") || header.includes("الخصم");
      const isDiscountField = /discount/i.test(field);
      return !(isDiscountHeader || isDiscountField);
    });
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="view-product-order">
      {isWholeOrderCanceled() && (
        <div style={{ marginBottom: 20 }}>
          <Alert
            severity="error"
            icon={<CancelIcon />}
            sx={{ fontSize: 16, fontWeight: "bold" }}
          >
            تم إلغاء هذا الطلب بالكامل
          </Alert>
        </div>
      )}

      <div className="header">
        <div className="right">
          <div className="order-info">
            <div className="info">
              <h3>
                رقم الطلب: {orderData?.order_number || "N/A"}
                {isWholeOrderCanceled() && (
                  <span style={{ color: "#d32f2f", marginRight: 10 }}>
                    (ملغي)
                  </span>
                )}
              </h3>
              <p>{formatDate(orderData?.created_at)}</p>
              <span>
                طريقة الدفع:{" "}
                {orderData?.payment_type === "visa"
                  ? "فيزا"
                  : orderData?.payment_type || "N/A"}
              </span>
            </div>

            <div className="admin-share">
              <span>المبلغ المستحق للسهم العربي</span>
              <p>{orderData?.sahm_amount || "0"} جنية</p>
            </div>

            <div className="order-total">
              <span>اجمالي تكلفة الطلب</span>
              <p>{orderData?.total || "0"} جنية</p>
            </div>

            <div className="btn">
              {isWholeOrderCanceled() ? (
                <ColorButton
                  size="large"
                  color="#9e9e9e"
                  text="تم إلغاء الطلب"
                  icon={<CancelIcon />}
                  disabled
                />
              ) : (
                <ColorButton
                  size="large"
                  color="#E63050"
                  text="الغاء الطلب بالكامل"
                  disabled={!canCancelWholeOrder()}
                  loading={actionLoading}
                  onClick={handleCancelWholeOrder}
                />
              )}
            </div>
          </div>

          <div className="user-info">
            <div className="user">
              <p className="title">المستخدم</p>
              <div className="user-container">
                <div className="info">
                  <img src={Avatr} alt="" />
                  <div className="wrapper">
                    <p>{orderData?.address?.name || "غير متوفر"}</p>
                    <span>{orderData?.address?.phone || "غير متوفر"}</span>
                  </div>
                </div>
                <button
                  onClick={handleLocationClick}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 5,
                    borderRadius: 4,
                  }}
                  title="عرض الموقع على الخريطة"
                >
                  <img
                    src={Location}
                    alt="عرض الموقع"
                    style={{ width: 24, height: 24 }}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* LEFT invoice box */}
        <div className="left">
          <div className="total">
            <p className="title">
              الفاتورة
              {isWholeOrderCanceled() && (
                <span
                  style={{ color: "#d32f2f", fontSize: 14, marginRight: 10 }}
                >
                  (ملغي)
                </span>
              )}
            </p>

            <div className="item">
              <span>قيمة المنتجات</span>
              <span>{orderData?.subtotal || "0"} جنية</span>
            </div>

            {(couponCode || couponDiscountAmount) && (
              <>
                <div className="item">
                  <span>كود القسيمة</span>
                  <span>{couponCode ?? "-"}</span>
                </div>
                <div className="item">
                  <span>الخصم على الفاتورة</span>
                  <span>{couponDiscountAmount ?? 0} جنية</span>
                </div>
              </>
            )}

            <div className="item">
              <span>الضريبة</span>
              <span>{orderData?.tax || "0"} جنية</span>
            </div>
            <div className="item">
              <span>تكلفة التوصيل</span>
              <span>{orderData?.delivery_cost || "0"} جنية</span>
            </div>

            {/* removed per-item discount row */}

            <div className="item">
              <span>الإجمالي</span>
              <span>{orderData?.total || "0"} جنية</span>
            </div>

            <div className="btn">
              <ColorButton
                size="medium"
                color="#636270"
                text="طباعة الفاتورة"
                icon={<PrintIcon />}
                onClick={() => handlePrintInvoice()}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bills-container">
        {orderData?.vendor_products?.map((vendorProduct, index) => (
          <div
            className="bill"
            key={vendorProduct?.vendor?.id || index}
            style={
              isWholeOrderCanceled()
                ? { opacity: 0.7, backgroundColor: "#f5f5f5" }
                : {}
            }
          >
            <div className="id">{index + 1}</div>
            <div className="bill-wrapper">
              <div className="bill-info">
                <div className="info">
                  <img
                    src={vendorProduct?.vendor?.logo || Avatr}
                    alt={vendorProduct?.vendor?.name || "Vendor"}
                    onError={(e) => {
                      e.target.src = Avatr;
                    }}
                  />
                  <div className="wrapper">
                    <p>البائع: {vendorProduct?.vendor?.name || "غير متوفر"}</p>
                    <span>{vendorProduct?.vendor?.url || "غير متوفر"}</span>
                  </div>
                </div>
                <div className="actions-btns">
                  <ColorButton
                    size="medium"
                    color="#636270"
                    text="طباعة الفاتورة"
                    icon={<PrintIcon />}
                    onClick={() => handlePrintInvoice(vendorProduct)}
                  />
                  <ColorButton
                    size="large"
                    color="#039CED"
                    text="ارسال تنبية بالطلب"
                    icon={<NotificationsIcon />}
                    loading={
                      notificationLoading[vendorProduct?.vendor?.id] || false
                    }
                    disabled={!canSendNotification(vendorProduct)}
                    onClick={() =>
                      handleSendNotification(vendorProduct?.vendor?.id)
                    }
                  />
                  {isWholeOrderCanceled() ? (
                    <ColorButton
                      size="medium"
                      color="#9e9e9e"
                      text="الطلب ملغي"
                      icon={<CancelIcon />}
                      disabled
                    />
                  ) : vendorProduct?.cancel_order ? (
                    <ColorButton
                      size="medium"
                      color="#E63050"
                      text="تم الغاء الطلب"
                      disabled
                    />
                  ) : (
                    <ColorButton
                      size="medium"
                      color="#E63050"
                      text="الغاء الطلب"
                      loading={actionLoading}
                      disabled={!canCancelOrder(vendorProduct)}
                      onClick={() =>
                        handleCancelVendorOrder(vendorProduct?.vendor?.id)
                      }
                    />
                  )}
                </div>
              </div>

              <div className="data-grid">
                <DataGrid
                  sx={{
                    backgroundColor: "white",
                    width: "auto",
                    ...(isWholeOrderCanceled() && {
                      "& .MuiDataGrid-row": { backgroundColor: "#fafafa" },
                    }),
                  }}
                  autoHeight
                  columns={filteredOrderColumns}
                  rows={(vendorProduct?.products || []).map((p, i) => ({
                    ...p,
                    id: p.variations?.id ? `${p.id}_var_${p.variations.id}` : `${p.id}_${i}`,
                  }))}
                  disableVirtualization
                  disableColumnFilter
                  disableColumnMenu
                />
              </div>
            </div>

            <div className="status">
              <Stepper
                order={vendorProduct}
                orderId={id}
                wholeOrderCanceled={orderData?.status}
              />
            </div>
          </div>
        ))}

        {(!orderData?.vendor_products ||
          orderData.vendor_products.length === 0) && (
          <div className="no-products">
            <Alert severity="info">لا توجد منتجات لهذا الطلب</Alert>
          </div>
        )}
      </div>

      <LocationMapDialog
        open={locationDialogOpen}
        onClose={handleCloseLocationDialog}
        lat={orderData?.address?.lat}
        lng={orderData?.address?.lng}
        address={orderData?.address?.address}
        customerName={orderData?.address?.name}
      />
    </div>
  );
};

export default ViewProductOrder;
